import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, formatDate, productSummary } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import InlineStatus from "@/components/InlineStatus";
import { FABRICACION } from "@/lib/statuses";
import { toast } from "sonner";
import { Factory, Printer, Search, StickyNote, ExternalLink, Camera, X, Trash2, ChevronLeft, ChevronRight, ListTree, FileSpreadsheet, FileText } from "lucide-react";
import { isArchived, historyCounts } from "@/lib/archive";
import HistoryCounter from "@/components/HistoryCounter";
import { BaseBadge } from "@/components/bases/BasesComun";
import EditableNote from "@/components/EditableNote";
import { FabricacionExport } from "@/components/FabricacionExport";
import { sortManual, useManualOrder, MoveHandle, PRIO_ROW } from "@/lib/ordenManual";
import { PrioBadge } from "@/components/PrioridadToggle";
import { ResumenFabricacion } from "@/components/ResumenFabricacion";
import { FabricadosDelDia } from "@/components/FabricadosDelDia";
import { CosturaBadge } from "@/components/CosturaSelect";
import { descargar } from "@/lib/download";
import { FabUnidades } from "@/components/FabUnidades";
import { ImprimirEtiquetas } from "@/components/domicilios/CargaEntrega";

const OrdenEstado = ({ o }) => {
  const e = FABRICACION.find((x) => x.value === (o.estado_fabricacion || "pendiente")) || FABRICACION[0];
  return <span className={`text-xs font-semibold rounded px-2 py-1 whitespace-nowrap ${e.cls}`} title="Se calcula con el estado de cada unidad" data-testid={`fab-orden-estado-${o.numero}`}>Pedido: {e.label}</span>;
};

export default function Fabricacion() {
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [view, setView] = useState("activos");
  const [showArchived, setShowArchived] = useState(false);
  const [resumenOpen, setResumenOpen] = useState(false);
  const [costuraMap, setCosturaMap] = useState({});

  useEffect(() => {
    if (view !== "activos") return;
    api.get("/costuras", { params: { vista: "todos" } })
      .then(({ data }) => setCosturaMap(Object.fromEntries((data || []).map((r) => [`${r.order_id}|${r.key}`, r.estado]))))
      .catch(() => {});
  }, [view, orders]);
  const costuraDe = (o, i, it) => costuraMap[`${o.id}|${i}:${it.product_id || ""}`];

  const load = async () => {
    try {
      // OPTIMIZACIÓN: filtrar en el servidor por estado de fabricación (pendiente|en_proceso|listo)
      // en lugar de traer TODO el histórico y filtrar en el navegador.
      const params = view === "historial"
        ? { estado_fabricacion: "listo", page: 1, page_size: 1000 }
        : { estado_fabricacion: "activos", page: 1, page_size: 5000 };
      const { data } = await api.get("/orders", { params });
      const raw = Array.isArray(data) ? data : (data.items || []);
      const relevant = raw.filter((o) => o.estado !== "cancelado");
      setOrders(relevant);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { if (view !== "fabricados") load(); }, [view]);

  const hoyGT = new Date().toLocaleDateString("en-CA", { timeZone: "America/Guatemala" });
  const gtDate = (ts) => (ts ? new Date(ts).toLocaleDateString("en-CA", { timeZone: "America/Guatemala" }) : "");
  const gtDT = (ts) => (ts ? new Date(ts).toLocaleString("es-GT", { timeZone: "America/Guatemala", dateStyle: "short", timeStyle: "short" }) : "");
  const historicos = orders.filter((o) => o.estado_fabricacion === "listo");
  // Historial = fabricados en días anteriores (hora Guatemala). Los de hoy están en "Fabricados del día".
  const historialDias = historicos.filter((o) => gtDate(o.estado_fabricacion_at) < hoyGT);
  const historialActivos = historialDias.filter((o) => !isArchived(o, "estado_fabricacion_at", 48));
  const historialArchivados = historialDias.filter((o) => isArchived(o, "estado_fabricacion_at", 48));

  const byView = view === "historial"
    ? (showArchived ? historialDias : historialActivos)
    : orders.filter((o) => (o.estado_fabricacion || "pendiente") !== "listo");
  const s = q.trim().toLowerCase();
  const filtered = s
    ? byView.filter((o) =>
        (o.numero || "").toLowerCase().includes(s) ||
        (o.cliente_nombre || "").toLowerCase().includes(s) ||
        (o.items || []).some((it) => (it.product_name || "").toLowerCase().includes(s))
      )
    : byView;

  const totales = {
    piezas: filtered.reduce((n, o) => n + (o.estado_fabricacion === "parcial" && o.fab_parcial
      ? (o.items || []).reduce((m, it, i) => m + Math.max(0, (it.cantidad || 0) - (o.fab_parcial[`${i}:${it.product_id || ""}`] || 0)), 0)
      : (o.total_qty || 0)), 0),
  };
  const ordered = sortManual(filtered, "fabricacion");
  const { move, handleProps, rowProps } = useManualOrder("fabricacion", ordered, setOrders);
  const canMove = !s;

  return (
    <div className="space-y-6" data-testid="fabricacion-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Producción</div>
          <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
            <Factory className="w-8 h-8 text-[#8B5CF6]" /> Fabricación
          </h1>
          <p className="text-zinc-500 mt-2 text-sm">
            Lista de pedidos en cola. Cambia el estado desde la columna &quot;Estado&quot;.
          </p>
        </div>
        <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setResumenOpen(true)}
          className="inline-flex items-center gap-2 text-sm font-semibold border border-zinc-200 rounded-md px-3 py-2 hover:bg-zinc-50"
          data-testid="fab-resumen-btn"
        >
          <ListTree className="w-4 h-4" /> Resumen
        </button>
        <Link
          to="/fabricacion/imprimir"
          className="inline-flex items-center gap-2 text-sm font-semibold border border-zinc-200 rounded-md px-3 py-2 hover:bg-zinc-50"
          data-testid="print-fab-lote-btn"
        >
          <Printer className="w-4 h-4" /> Imprimir lote
        </Link>
        </div>
      </header>
      <ResumenFabricacion open={resumenOpen} onOpenChange={setResumenOpen} />

      <FabricacionExport />

      {/* View tabs */}
      <div className="flex flex-wrap items-center gap-2 border-b border-zinc-200 -mb-2">
        {[
          { v: "activos", label: "Activos del día", count: view === "activos" ? orders.filter((o) => (o.estado_fabricacion || "pendiente") !== "listo").length : null },
          { v: "fabricados", label: "Fabricados del día", count: null },
          { v: "historial", label: "Historial", count: view === "historial" ? historialActivos.length : null },
        ].map((t) => (
          <button
            key={t.v}
            type="button"
            onClick={() => setView(t.v)}
            data-testid={`fab-view-${t.v}`}
            className={`px-3 py-2 text-sm border-b-2 transition-colors -mb-px ${
              view === t.v ? "border-[#8B5CF6] text-[#8B5CF6] font-semibold" : "border-transparent text-zinc-500 hover:text-zinc-900"
            }`}
          >
            {t.label}
            {t.count !== null && <span className="ml-1 text-[10px] bg-zinc-100 rounded-full px-1.5 py-0.5">{t.count}</span>}
          </button>
        ))}
        {view === "historial" && historialArchivados.length > 0 && (
          <label className="ml-auto text-xs text-zinc-600 flex items-center gap-2 cursor-pointer" data-testid="fab-archive-toggle">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
            Incluir anteriores <span className="text-zinc-400">({historialArchivados.length})</span>
          </label>
        )}
        {view === "activos" && (
          <div className="ml-auto flex gap-2 pb-1">
            <Button variant="outline" size="sm" className="gap-1" onClick={() => descargar("/fabricacion/activos.xlsx", `activos_fabricacion_${hoyGT}.xlsx`, { ids: ordered.map((o) => o.id) }).catch((e) => toast.error(String(e.message || e)))} data-testid="fab-activos-xlsx"><FileSpreadsheet className="w-4 h-4" /> Excel activos</Button>
            <Button variant="outline" size="sm" className="gap-1" onClick={() => descargar("/fabricacion/activos.pdf", `activos_fabricacion_${hoyGT}.pdf`, { ids: ordered.map((o) => o.id) }).catch((e) => toast.error(String(e.message || e)))} data-testid="fab-activos-pdf"><FileText className="w-4 h-4" /> PDF activos</Button>
          </div>
        )}
      </div>

      {view === "fabricados" ? <FabricadosDelDia fuente="fabricacion" canEdit /> : (<>
      {/* Summary bar */}
      {view === "historial" && (
        <HistoryCounter counts={historyCounts(historialDias, "estado_fabricacion_at")} label="Fabricados" />
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{view === "historial" ? "Pedidos fabricados" : "Pedidos en cola"}</div>
          <div className="font-manrope font-black text-3xl mt-1">{filtered.length}</div>
        </Card>
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{view === "historial" ? "Piezas fabricadas" : "Piezas a producir"}</div>
          <div className="font-manrope font-black text-3xl mt-1">{totales.piezas}</div>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por pedido, cliente o producto..." className="pl-9" data-testid="fab-search" />
      </div>

      {/* List (table on desktop, cards on mobile) */}
      <Card className="border-zinc-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr className="text-left">
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Pedido</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Cliente</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Vendedor</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Productos</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Notas</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Estado</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-zinc-400 py-12">
                    <Factory className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
                    No hay pedidos en fabricación.
                  </td>
                </tr>
              )}
              {ordered.map((o, i) => (
                <tr key={o.id} {...rowProps(i)} className={`align-top ${o.prioridad_fabricacion ? PRIO_ROW : "hover:bg-zinc-50"}`} data-testid={`fab-row-${o.numero}`}>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-start gap-2">
                    {canMove && <MoveHandle i={i} n={ordered.length} move={move} handleProps={handleProps} testid={`fab-move-${o.numero}`} />}
                    <div>
                    <div className="font-mono text-xs font-semibold">{o.numero}</div>
                    {view === "historial" && o.estado_fabricacion_at && <div className="text-[10px] text-emerald-700" data-testid={`fab-hist-at-${o.numero}`}>Fabricado: {gtDT(o.estado_fabricacion_at)}</div>}
                    <PrioBadge show={o.prioridad_fabricacion} />
                    <div className="text-[11px] text-zinc-500 mt-0.5">{formatDate(o.fecha)}</div>
                    {o.fecha_entrega && (
                      <div className="text-[11px] text-orange-700 mt-0.5">Entrega: {formatDate(o.fecha_entrega)}</div>
                    )}
                    </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 min-w-[160px]">
                    <div className="font-semibold">{o.cliente_nombre}</div>
                    {o.cliente_telefono && <div className="text-[11px] text-zinc-500">{o.cliente_telefono}{o.cliente_telefono2 ? <span className="text-zinc-400"> · alt {o.cliente_telefono2}</span> : null}</div>}
                  </td>
                  <td className="px-4 py-3 text-zinc-600 text-xs whitespace-nowrap">
                    {o.creado_por || "—"}
                  </td>
                  <td className="px-4 py-3 min-w-[320px] max-w-md">
                    {view === "activos" ? <FabUnidades o={o} onChanged={load} /> : (
                    <ul className="space-y-1.5">
                      {(o.items || []).map((it, i) => (
                        <li key={i} className="text-xs flex items-start gap-2" data-testid={`fab-item-summary-${o.numero}-${i}`}>
                          <span className="inline-block min-w-[28px] text-right font-mono font-bold text-zinc-900">{it.cantidad}×</span>
                          <span className="flex-1 font-medium text-zinc-900 uppercase tracking-tight leading-snug">
                            {productSummary(it)}
                            {o.estado_fabricacion === "parcial" && (o.fab_parcial || {})[`${i}:${it.product_id || ""}`] > 0 && (
                              <span className="ml-1 normal-case text-[10px] font-semibold text-amber-800 bg-amber-100 rounded px-1" data-testid={`fab-hechas-${o.numero}-${i}`}>{o.fab_parcial[`${i}:${it.product_id || ""}`]} hechas · faltan {it.cantidad - o.fab_parcial[`${i}:${it.product_id || ""}`]}</span>
                            )}
                            {costuraDe(o, i, it) && <span className="ml-1 normal-case"><CosturaBadge estado={costuraDe(o, i, it)} /></span>}
                            {(o.bases_estado || {})[`${i}:${it.product_id || ""}`] && <span className="ml-1 normal-case"><BaseBadge info={o.bases_estado[`${i}:${it.product_id || ""}`]} /></span>}
                          </span>
                        </li>
                      ))}
                    </ul>)}
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-2">
                      {o.items?.length || 0} producto{o.items?.length === 1 ? "" : "s"} · {o.total_qty || 0} unidades
                    </div>
                  </td>
                  <td className="px-4 py-3 max-w-[220px] min-w-[180px]">
                    <EditableNote
                      orderId={o.id}
                      modulo="notas_fab" initial={o.notas_fab ?? o.notas_hoja ?? ""}
                      onSaved={(v) => setOrders((prev) => prev.map((x) => x.id === o.id ? { ...x, notas_fab: v } : x))}
                    />
                    {o.notas && (
                      <div className="mt-1 text-[10px] text-zinc-500 italic border-l-2 border-zinc-300 pl-2">
                        <StickyNote className="w-3 h-3 inline mr-1" />
                        {o.notas}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {view === "activos" ? <><OrdenEstado o={o} /><ImprimirEtiquetas orderId={o.id} compact className="mt-2" /></> : (
                    <InlineStatus
                      orderId={o.id}
                      campo="estado_fabricacion"
                      value={o.estado_fabricacion || "pendiente"}
                      options={FABRICACION}
                      canEdit
                      onChanged={load}
                    />)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <FabFotosButton orderId={o.id} count={o.fabricacion_fotos_count ?? (o.fabricacion_fotos || []).length} onChange={load} />
                      <Link to={`/pedidos/${o.id}`} onClick={() => { try { sessionStorage.setItem("pedidos_return_path", "/fabricacion"); } catch {} }} className="text-[#002FA7] text-xs font-semibold uppercase tracking-widest inline-flex items-center gap-1">
                        Ver <ExternalLink className="w-3 h-3" />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile stacked list */}
        <div className="md:hidden divide-y divide-zinc-100">
          {filtered.length === 0 && (
            <div className="text-center text-zinc-400 py-12 text-sm">No hay pedidos en fabricación.</div>
          )}
          {ordered.map((o, i) => (
            <div key={o.id} {...rowProps(i)} className={`p-4 ${o.prioridad_fabricacion ? PRIO_ROW : ""}`} data-testid={`fab-card-${o.numero}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-start gap-2">
                {canMove && <MoveHandle i={i} n={ordered.length} move={move} handleProps={handleProps} testid={`fab-mmove-${o.numero}`} />}
                <div>
                  <div className="font-mono text-xs font-semibold">{o.numero}</div>
                  <PrioBadge show={o.prioridad_fabricacion} />
                  <div className="font-semibold">{o.cliente_nombre}</div>
                  {o.creado_por && <div className="text-[11px] text-zinc-500">Vendedor: {o.creado_por}</div>}
                </div>
                </div>
                {view === "activos" ? <div className="space-y-1"><OrdenEstado o={o} /><ImprimirEtiquetas orderId={o.id} compact /></div> : (
                <InlineStatus
                  orderId={o.id}
                  campo="estado_fabricacion"
                  value={o.estado_fabricacion || "pendiente"}
                  options={FABRICACION}
                  canEdit
                  onChanged={load}
                />)}
              </div>
              {view === "activos" ? <div className="mt-2"><FabUnidades o={o} onChanged={load} /></div> : (
              <ul className="mt-2 text-xs space-y-1">
                {(o.items || []).map((it, i) => (
                  <li key={i} className="uppercase tracking-tight"><span className="font-mono font-bold">{it.cantidad}×</span> {productSummary(it)}{o.estado_fabricacion === "parcial" && (o.fab_parcial || {})[`${i}:${it.product_id || ""}`] > 0 && <span className="ml-1 normal-case text-[10px] font-semibold text-amber-800 bg-amber-100 rounded px-1">{o.fab_parcial[`${i}:${it.product_id || ""}`]} hechas · faltan {it.cantidad - o.fab_parcial[`${i}:${it.product_id || ""}`]}</span>}{costuraDe(o, i, it) && <span className="ml-1 normal-case"><CosturaBadge estado={costuraDe(o, i, it)} /></span>}{(o.bases_estado || {})[`${i}:${it.product_id || ""}`] && <span className="ml-1 normal-case"><BaseBadge info={o.bases_estado[`${i}:${it.product_id || ""}`]} /></span>}</li>
                ))}
              </ul>)}
              <div className="mt-2">
                <EditableNote
                  orderId={o.id}
                  modulo="notas_fab" initial={o.notas_fab ?? o.notas_hoja ?? ""}
                  onSaved={(v) => setOrders((prev) => prev.map((x) => x.id === o.id ? { ...x, notas_fab: v } : x))}
                />
              </div>
              {o.notas && (
                <div className="text-xs bg-yellow-50 border border-yellow-200 rounded p-2 mt-2">
                  <StickyNote className="w-3 h-3 inline mr-1 text-yellow-700" /> {o.notas}
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
      </>)}
    </div>
  );
}

function FabFotosButton({ orderId, count, onChange }) {
  const [open, setOpen] = useState(false);
  const [fotos, setFotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}/fabricacion-fotos`);
      setFotos(data);
    } catch {}
  };
  const openDialog = async () => { await load(); setOpen(true); };
  const onFile = async (file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) return toast.error("Máx 6MB");
    setBusy(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`/orders/${orderId}/fabricacion-fotos`, { foto_base64: reader.result });
        toast.success("Foto guardada");
        await load();
        onChange?.();
      } catch (e) { toast.error(formatApiError(e)); }
      setBusy(false);
    };
    reader.readAsDataURL(file);
  };
  const del = async (fid) => {
    if (!window.confirm("¿Eliminar foto?")) return;
    try {
      await api.delete(`/orders/${orderId}/fabricacion-fotos/${fid}`);
      toast.success("Eliminada");
      await load();
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <>
      <button
        onClick={openDialog}
        className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded border border-zinc-300 hover:border-blue-400 hover:bg-blue-50"
        title="Fotos cabecera final"
        data-testid={`fab-fotos-btn-${orderId}`}
      >
        <Camera className="w-3 h-3" /> {count || 0}
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-md p-4 max-w-3xl max-h-[90vh] overflow-y-auto min-w-[560px]" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold">Fotos de cabecera final</h3>
              <button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <input type="file" accept="image/jpeg,image/png,image/webp" disabled={busy} onChange={(e) => onFile(e.target.files?.[0])} className="mb-4 text-xs" data-testid={`fab-fotos-input-${orderId}`} />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {fotos.length === 0 && <div className="col-span-3 text-center text-zinc-400 py-6">Sin fotos.</div>}
              {fotos.map((f) => (
                <div key={f.id} className="border border-zinc-200 rounded-md p-2 relative">
                  <img src={f.foto_base64} alt="cabecera" className="max-h-40 w-full object-contain rounded" />
                  <button onClick={() => del(f.id)} className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded" title="Eliminar"><Trash2 className="w-3 h-3" /></button>
                  <div className="text-[10px] text-zinc-500 mt-1">{(f.uploaded_at || "").slice(0, 19).replace("T", " ")}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
